import config from "../data/config.json";
import { isValueNull, withLoadedModifiedDate } from "@1823-partners/core";

// get_latest synthesizes CRE-owned rows from the CRE app; they have no stored
// row to check, cannot be saved here and are edited in the CRE app.
export const READ_ONLY_OWNER = 'CRE';
export const isReadOnlySecMaster = (row) => row?.SECURITY_OWNER === READ_ONLY_OWNER;

// Loan fields get_latest only fills for CRE rows, from BEACON.CRE_DB. SEC_MASTER
// has no such columns (UAT DESCRIBE, 2026-10-20), so a value set here could never
// be saved: they are display-only on every row and are kept out of
// formatSecMasterData's whitelist, imports and duplicates.
export const CRE_ONLY_FIELDS = [
  'BORROWER', 'SERVICE_FEE', 'RECOURSE_TYPE', 'ORIGINAL_LOAN_TERM', 'INTEREST_ONLY_TERM',
];
export const isCreOnlyField = (field) => CRE_ONLY_FIELDS.includes(field);

// The fields of an imported row the grid takes: known SecMaster fields, minus the
// CRE-only ones (an imported value would show in the grid but never save).
export const pickImportFields = (row, knownFields) =>
  Object.fromEntries(
    Object.entries(row).filter(([key]) => knownFields[key] && !isCreOnlyField(key))
  );

// New grid rows carry a synthetic id until the user types the real CUSIP/PPN.
export const isSyntheticCusipPpn = (value) =>
  typeof value === 'string' && value.startsWith('new_row_');

/**
 * Rating to NAIC Designation mapping
 */
const RATING_TO_NAIC_MAP = {
  "AAA": "1.A",
  "Aa1": "1.B",
  "Aa2": "1.C",
  "Aa3": "1.D",
  "A1": "1.E",
  "A2": "1.F",
  "A3": "1.G",
  "Baa1": "2.A",
  "Baa2": "2.B",
  "Baa3": "2.C",
  "Ba1": "3.A",
  "Ba2": "3.B",
  "Ba3": "3.C",
  "B1": "4.A",
  "B2": "4.B",
  "B3": "4.C",
  "Caa1": "5.A",
  "Caa2": "5.B",
  "Caa3": "5.C",
  "AA+": "1.B",
  "AA": "1.C",
  "AA-": "1.D",
  "A+": "1.E",
  "A": "1.F",
  "A-": "1.G",
  "BBB+": "2.A",
  "BBB": "2.B",
  "BBB-": "2.C",
  "BB+": "3.A",
  "BB": "3.B",
  "BB-": "3.C",
  "B+": "4.A",
  "B": "4.B",
  "B-": "4.C",
  "CCC+": "5.A",
  "CCC": "5.B",
  "CCC-": "5.C",
  "CM1": "CM1",
  "CM2": "CM2",
  "CM3": "CM3",
  "NR": "Z"
};

/**
 * Gets the NAIC designation for a given rating
 * @param {string} rating - The rating value
 * @returns {string|null} - The NAIC designation or null if rating not found
 */
export const getNaicDesignationFromRating = (rating) => {
  if (!rating || typeof rating !== 'string') {
    return null;
  }
  const trimmedRating = rating.trim();
  return RATING_TO_NAIC_MAP[trimmedRating] || null;
};

/**
 * Asset Type to Collateral Type mapping
 */
const ASSET_TYPE_TO_COLLATERAL_TYPE_MAP = {
  "ABS - Aircraft": "ABS - Aircraft Leases",
  "ABS - Auto": "ABS - Auto Loans",
  "ABS - Cell Tower": "ABS - Cell Tower Leases",
  "ABS - Consumer": "ABS - Consumer Loans",
  "ABS - Container": "ABS - Container Leases",
  "ABS - Credit Card": "ABS - Credit Card Receivables",
  "ABS - Datacenter": "ABS - Data Center Leases",
  "ABS - Equipment Loan": "ABS - Equipment Backed Loans",
  "ABS - Fiber": "ABS - Other",
  "ABS - Home Improvement": "ABS - Other",
  "ABS - Litigation": "ABS - Other",
  "ABS - Manufactured Housing": "ABS - Manufactured Housing Loans",
  "ABS - MCA": "ABS - Advance Receivables",
  "ABS - MSR": "ABS - Other",
  "ABS - NNN": "ABS - Other",
  "ABS - Other": "ABS - Other",
  "ABS - PDP": "ABS - Other",
  "ABS - PPA": "ABS - Other",
  "ABS - Railcar": "ABS - Railcar Leases",
  "ABS - Rate Reduction Bonds": "ABS - Other",
  "ABS - Rental Car": "ABS - Rental Car Leases",
  "ABS - SBC": "ABS - Other",
  "ABS - Servicer Advance": "ABS - Other",
  "ABS - SLAB": "ABS - Student Loans",
  "ABS - Small Business": "ABS - Small Business Administration",
  "ABS - Solar": "ABS - Solar Loans",
  "ABS - Structured Settlement": "ABS - Structured Settlements",
  "ABS - Utility": "ABS - Stranded Utilities",
  "ABS - WBS": "ABS - Whole Business",
  "Agency CMBS": "Commercial Mortgage Backed Securities",
  "Agency CMO": "Collateralized Mortgage Obligation (CMO)",
  "CBO": "Collateralized Bond Obligation (CBO)",
  "CLO": "Collateralized Loan Obligation (CLO)",
  "Conduit CMBS": "CMBS - Conduit/Fusion",
  "Corporate - EETC": "Enhanced Equipment Trust Certificate",
  "CRE CLO": "Commercial Real Estate (CRE) Collateralized Loan Obligation (CLO)",
  "Home Equity Contracts": "Home Equity Loans",
  "MM CLO": "CLO - Small and Middle Market",
  "Non-Agency CRT": "Residential Mortgage Backed Securities",
  "Non-Agency Jumbo": "RMBS - Jumbo",
  "Non-Agency Legacy": "Residential Mortgage Backed Securities",
  "Non-Agency Non-QM": "RMBS - Non-QM Loans",
  "Non-Agency Reverse Mortgage": "Reverse Mortgage",
  "Non-Agency RPL": "RMBS - Reperforming Loans",
  "Non-Agency RTL": "Residential Mortgage Backed Securities",
  "SASB CMBS": "CMBS - Single Asset/Borrower",
  "TruPs CDO": "CDO - Trust Preferred"
};

/**
 * Gets the Collateral Type for a given Asset Type
 * @param {string} assetType - The asset type value
 * @returns {string|null} - The collateral type or null if asset type not found
 */
export const getCollateralTypeFromAssetType = (assetType) => {
  if (!assetType || typeof assetType !== 'string') {
    return null;
  }
  const trimmedAssetType = assetType.trim();
  return ASSET_TYPE_TO_COLLATERAL_TYPE_MAP[trimmedAssetType] || null;
};

// isValueNull is imported from @1823-partners/core (top of file) and re-exported here
// for backwards compatibility with consumers importing from secMasterUtils
export { isValueNull };

/**
 * Formats sec master data for submission to the API
 * Only includes fields that exist in the SEC_MASTER table definition
 * @param {Object} secMaster - The sec master data to format
 * @returns {Object} - The formatted sec master data with only table fields
 */
export const formatSecMasterData = (secMaster) => {
  // Define allowed fields based on SEC_MASTER table definition
  const allowedFields = [
    'CW_ACCOUNT_NAME',
    'CW_ACCOUNT_NUMBER',
    'CUSTODIAN_NAME',
    'CUSTODIAN_ACCOUNT_NUMBER',
    'BROKER_DEALER',
    'ISSUER',
    'SECURITY_DESCRIPTION',
    'SECURITY_OWNER',
    'CUSIP_PPN',
    'TICKER',
    'STAT_CURRENT_UNITS',
    'STAT_BOOK_VALUE',
    'TOTAL_COMMITMENT',
    'TRADE_DATE',
    'SETTLE_DATE',
    'SETTLEMENT_TYPE',
    'CLOSING_CONTACT',
    'MATURITY_DATE',
    'TRADE_PRICE',
    'SECURITY_TYPE',
    'ASSET_TYPE',
    'STAT_CLASSIFICATION',
    'NAIC_GENERAL_CATEGORY',
    'COUPON_TYPE',
    'FLOATER',
    'FLOATER_SPREAD',
    'REFERENCE_INDEX',
    'PIK_INTEREST',
    'COUPON_FREQUENCY',
    'FIRST_COUPON_DATE',
    'DAY_COUNT',
    'BALLOON_PAYMENT',
    'ILLIQUID',
    'RATING',
    'NAIC_DESIGNATION',
    'RATING_AGENCY',
    'PRIVATE_RATING',
    'RATING_DATE',
    'RATING_ESTIMATE',
    'COUNTRY_OF_ISSUE',
    'AFFILIATED_UNAFFILIATED',
    'CONNECTED_ENTITY',
    'MODIFIED_DATE',
    'MODIFIED_BY',
    'COLLATERAL_TYPE',
    'ALL_IN_RATE',
    'BASE_RATE',
    'STAT_BOOK_YIELD',
    'COMMENTS',
    'STATUS',
    'AMORTIZATION_TYPE',
    'ACCRUED_INTEREST_START_DATE',
    'SECURITY_OWNER_TEAM',
    'CURRENCY'
  ];

  // Filter to only include allowed fields
  const formattedSecMaster = {};
  allowedFields.forEach(field => {
    if (secMaster.hasOwnProperty(field)) {
      formattedSecMaster[field] = secMaster[field];
    }
  });

  // Convert empty string fields to null (except booleans which should be false/null)
  Object.keys(formattedSecMaster).forEach(key => {
    const booleanFields = ['FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE'];
    if (!booleanFields.includes(key) && formattedSecMaster[key] === '') {
      formattedSecMaster[key] = null;
    }
  });

  return formattedSecMaster;
};

// Text and nullable-boolean fields only: dates and numbers keep their typed
// '' -> null normalization.
const NON_TEXT_FIELDS = new Set([
  'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'TOTAL_COMMITMENT', 'TRADE_PRICE', 'FLOATER_SPREAD',
  'ALL_IN_RATE', 'BASE_RATE', 'STAT_BOOK_YIELD', 'TRADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE',
  'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE', 'MODIFIED_DATE', 'MODIFIED_BY',
]);
const isBlank = (value) => value === null || value === undefined || value === '';

/**
 * The grid shows NULL as ''. Put back the stored NULL or '' of a text/boolean field the
 * user did not change, so a save does not rewrite untouched values.
 * `baseline` is { raw, display, touched } for an existing row, null for a new one:
 * raw is the row as loaded, display what the grid showed for it, touched the fields
 * with tracked edits. A field is untouched when it is not tracked and still equals
 * its displayed value, so an edited false or clear is never mistaken for a NULL.
 */
const restoreUntouchedBlanks = (formatted, row, baseline) => {
  if (!baseline?.raw) return formatted;
  const { raw, display = {}, touched = {} } = baseline;
  const restored = { ...formatted };
  Object.keys(restored).forEach((field) => {
    if (NON_TEXT_FIELDS.has(field) || !(field in raw) || !isBlank(raw[field])) return;
    if (Object.prototype.hasOwnProperty.call(touched, field)) return;
    if (isBlank(row[field]) && isBlank(display[field])) restored[field] = raw[field];
  });
  return restored;
};

/**
 * Formats rows for /save-secmaster or /delete-entry. The route writes every row
 * sent, so pass only changed rows. Each carries the MODIFIED_DATE it was loaded
 * with, never a locally edited one: `tokenFor(row)` returns that loaded value
 * (null for a new row). The writer stamps MODIFIED_BY and the new MODIFIED_DATE.
 * @param {Array} rows - Loaded (or new) sec master rows, including local edits
 * @param {Function} tokenFor - row -> loaded MODIFIED_DATE, or null
 * @returns {Array} - Formatted rows carrying their loaded MODIFIED_DATE
 */
export const formatSecMasterPayload = (rows, tokenFor = () => null, baselineFor = () => null) =>
  rows.map((row) =>
    withLoadedModifiedDate(
      restoreUntouchedBlanks(formatSecMasterData(row), row, baselineFor(row)),
      { MODIFIED_DATE: tokenFor(row) }
    )
  );

/**
 * Validates sec master data against required fields
 * @param {Object} secMasterData - The sec master data to validate
 * @param {boolean} isNewEntry - Whether this is a new entry (affects CUSIP_PPN validation)
 * @returns {Array} - Array of missing required field names
 */
export const validateSecMasterData = (secMasterData, isNewEntry = false) => {
  const emptyFields = config.validation.requiredFields.filter(field => {
    // Special handling for CUSIP_PPN - if it's a new entry, it's OK to be empty
    if (field === 'CUSIP_PPN' && isNewEntry) {
      return false;
    }
    return isValueNull(secMasterData[field]);
  });

  return emptyFields;
};

/**
 * Validates a list of sec master entries and returns validation errors
 * @param {Array} entries - Array of sec master entry objects to validate
 * @returns {Array} - Array of validation error objects with row, cusip, securityName, and emptyFields
 *   (`cusip` is null for a new row's synthetic `new_row_*` id, so the description identifies it)
 *
 * A synthetic `new_row_*` CUSIP_PPN counts as missing: the user has not entered
 * the real identifier yet, and saving it would store `new_row_*` as a CUSIP.
 */
export const validateSecMasterList = (entries) => {
  const errorEntries = [];
  entries.forEach((entry, idx) => {
    const cusipPpn = entry['CUSIP_PPN'];
    const isSyntheticId = isSyntheticCusipPpn(cusipPpn);
    const emptyFields = config.validation.requiredFields.filter(col =>
      isValueNull(entry[col]) || (col === 'CUSIP_PPN' && isSyntheticId)
    );
    if (emptyFields.length !== 0) {
      errorEntries.push({
        row: idx,
        cusip: isSyntheticId || isValueNull(cusipPpn) ? null : cusipPpn,
        securityName: isValueNull(entry['SECURITY_DESCRIPTION']) ? null : entry['SECURITY_DESCRIPTION'],
        emptyFields: emptyFields
      });
    }
  });

  return errorEntries;
};
